import type { Course } from '../course/Course';
import type { HeightField } from '../terrain/HeightField';
import type { SplatMapBaker } from './SplatMapBaker';
import { isPointInRegion, polygonCentroid, type Region } from '../utils/geometry';

/**
 * Real greens are graded smooth/flat relative to surrounding terrain —
 * this renderer does two things per hole, both sourced directly from
 * Hole.def.green (never inventing its own shape): paints the green's blend
 * weight into the shared splat map, and flattens the HeightField toward
 * that green's own average height so the mesh actually reads as a graded
 * putting surface, not just a differently-colored patch of rough terrain.
 */
export class GreenRenderer {
  private static readonly FLATTEN_FEATHER = 8;

  paintSplat(course: Course, baker: SplatMapBaker): void {
    for (const hole of course.holes) {
      baker.paintRegion(hole.def.green, 'g');
    }
  }

  sculpt(course: Course, heightField: HeightField): void {
    for (const hole of course.holes) {
      const target = heightField.averageHeightInRegion(hole.def.green);
      heightField.flattenRegionToward(hole.def.green, target, GreenRenderer.FLATTEN_FEATHER);
      this.applyPlayableContours(heightField, hole.def.green);
    }
  }

  private applyPlayableContours(heightField: HeightField, green: Region): void {
    const center = green.kind === 'circle' ? green.center : polygonCentroid(green.points);
    const data = heightField.gridData;
    for (let iz = 0; iz < heightField.gridRows; iz += 1) {
      for (let ix = 0; ix < heightField.gridCols; ix += 1) {
        const { x, z } = heightField.indexToWorld(ix, iz);
        if (!isPointInRegion({ x, z }, green)) continue;
        const dx = x - center.x;
        const dz = z - center.z;
        const tilt = dx * 0.012 + dz * -0.007;
        const contour = Math.sin(dx * 0.35) * 0.045 + Math.cos(dz * 0.28) * 0.035;
        data[iz * heightField.gridCols + ix] += tilt + contour;
      }
    }
  }
}
